import {
    AfterViewInit, ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren
} from "@angular/core";
import { NavigationService } from "~/app/core/navigation.service";
import { InteractionType } from "~/app/core/model/interaction-type";
import { InteractionComponent } from "~/app/core/model/interaction-component";
import { ApiService } from "~/app/core/api.service";
import { NotificationService } from "~/app/core/notification.service";
import * as Log from "~/app/core/log";
import { NetworkService } from "~/app/core/network.service";
import { Settings } from "~/app/core/model/settings";
import { UserProperty } from "~/app/core/model/user-property";
import { InteractionTimer } from "~/app/core/interaction.service";
import {CoreService} from "~/app/core/core.service";
import {BasePage} from "~/app/pages/base.page";

@Component({
    selector: "About",
    templateUrl: "./about.page.html",
    styleUrls: ["./about.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AboutPage extends BasePage implements OnInit, AfterViewInit, OnDestroy {
    os: string;
    version: string;
    pushToken: string;

    private wordTapExpire: number = 0;
    private wordTapStage: number = 0;
    private wordTapSequence: string = "";
    private pageTimer: InteractionTimer;

    private readonly userId: number;

    constructor(
        private readonly core: CoreService,
        private readonly cd: ChangeDetectorRef,
        private readonly net: NetworkService,
    ) {
        super();
        // Use the component constructor to inject providers.
        this.core.api.interactOther({ pgAboutCount: 1 });
        this.pageTimer = this.core.api.interactTimerOther("pgAboutTime");
        const userInfo = this.core.api.user_info();
        this.userId = userInfo ? userInfo.id : null;

        const settings = this.core.api.device_loadSettings();
        if (settings.testable) {
            this.wordTapStage = 4;
        }

        if (settings.testable && settings.testing) {
            this.core.notify.getPushToken().then((token) => {
                Log.out("=========> PUSH TOKEN: " + token);
                this.pushToken = token;
                this.cd.markForCheck();
            });
        }
    }

    ngOnInit(): void {
        this.os = this.core.platform.platforms().join(" ");
        this.version = "1.0";
    }

    ngAfterViewInit(): void {
        // this.htmlView.changes.subscribe((changes) => {
        //     if (this.htmlView.length > 0) {
        //         this.htmlView.first.nativeElement.requestLayout();
        //     }
        // });
    }

    ngOnDestroy() {
        this.pageTimer.log();
    }

    onShareAppTap(): void {
        this.core.api.interaction(InteractionType.ShareApp, InteractionComponent.AboutPage);
        this.core.nav.navPageShareApp();
    }

    onSupportTap(): void {
        this.core.api.interaction(InteractionType.ContactSupport, InteractionComponent.AboutPage);
        this.core.nav.navPageContactUs("support");
    }

    onMerchantTap(): void {
        this.core.api.interaction(InteractionType.ContactMerchant, InteractionComponent.AboutPage);
        this.core.nav.navPageContactUs("merchants");
    }

    onSponsorTap(): void {
        this.core.api.interaction(InteractionType.ContactSponsor, InteractionComponent.AboutPage);
        this.core.nav.navPageContactUs("sponsors");
    }

    tapPrivacyPolicy(): void {
        this.core.nav.navPagePrivacyPolicy();
    }

    tapTermsOfUse(): void {
        this.core.nav.navPageTermsOfUse();
    }

    wordTap(val: number): void {
        const now: number = new Date().getTime();
        if (this.wordTapExpire < now) {
            this.wordTapSequence = "";
        }
        this.wordTapSequence = this.wordTapSequence + String(val);
        this.wordTapExpire = now + 1000;

        this.validateWordTapSequence();
    }

    private validateWordTapSequence() {
        if (this.wordTapStage === 0) {
            if (this.wordTapSequence.endsWith("0000000000")) {
                this.core.toast.toastInfo({
                    header: "Hello?",
                    message: "You're persistent. What do you want to know about?",
                    duration: 2000
                });
                this.wordTapSequence = "";
                this.wordTapStage++;
            } else if (this.wordTapSequence.endsWith("12321232123")) {
                this.showSetEnv();
                this.wordTapSequence = "";
            } else if (this.wordTapSequence.endsWith("3333333333")) {
                if (this.settings.enableLogging) {
                    if (this.settings.logLevel !== "trace") {
                        // logging already enabled, so raise it to trace level logging
                        const lastSettings = this.settings;
                        const settings = this.settings;
                        settings.logLevel = "trace";
                        this.core.api.device_saveSettings(settings);
                        this.updateLogSettings(settings, lastSettings);

                        this.core.toast.toastInfo({
                            header: "More Logging?",
                            message: "Ok. You've enabled even more logging...",
                            duration: 2000
                        });
                    }
                } else {
                    const lastSettings = this.settings;
                    const settings = this.settings;
                    settings.enableLogging = true;
                    settings.logLevel = "debug";
                    settings.logBufferSize = 10000;
                    this.core.api.device_saveSettings(settings);
                    this.updateLogSettings(settings, lastSettings);

                    this.core.toast.toastInfo({
                        header: "Logging?",
                        message: "Ok. You've enabled local logging.",
                        duration: 2000
                    });
                }
                this.wordTapSequence = "";
                // don't advance the stage
            } else if (this.wordTapSequence.endsWith("1111111111")) {
                if (this.settings.enableLogging) {
                    // logging already enabled, so raise it to trace level logging
                    const lastSettings = this.settings;
                    const settings = this.settings;
                    settings.enableLogging = false;
                    this.core.api.device_saveSettings(settings);
                    this.updateLogSettings(settings, lastSettings);

                    this.core.toast.toastInfo({
                        header: "End Logging?",
                        message: "Ok. You've disabled local logging.",
                        duration: 2000
                    });
                }
                this.wordTapSequence = "";
                // don't advance the stage
            }
        } else if (this.wordTapStage === 1) {
            if (this.wordTapSequence.endsWith("123123")) {
                this.core.toast.toastInfo({
                    header: "Question?",
                    message: "Yes, those are facts about this app.",
                    duration: 2000
                });
                this.wordTapSequence = "";
                this.wordTapStage++;
            }
        } else if (this.wordTapStage === 2) {
            if (this.wordTapSequence.endsWith("2222222222")) {
                this.core.toast.toastSuccess({
                    header: "Test?",
                    message: "Ah. The app mode will now change to TEST. Do be careful.",
                    onHide: () => {
                        const settings = this.core.api.device_loadSettings();
                        settings.testable = true;
                        settings.bounceBackNotificationOverride = true;
                        settings.sponsorVideoSkipDelaySeconds = 2;
                        settings.bounceBackNotificationDelaySeconds = 60;
                        this.core.api.device_saveSettings(settings);
                        this.core.nav.navPageSettings();
                    }
                });
                this.wordTapStage++;
            } else if (this.wordTapSequence.length > 20) {
                this.core.toast.toastInfo({
                    header: "Want FREE stuff?",
                    message: "Well, we hope you're enjoying the app. Maybe you should explore the FREE stuff we have to offer!"
                });
                this.wordTapStage++;
            }
        } else if (this.wordTapStage === 3) {
            // do nothing
        } else if (this.wordTapStage === 4) {
            if (this.wordTapSequence.endsWith("2222222222")) {
                this.core.toast.toastSuccess({
                    header: "No More Test?",
                    message: "The app mode will now change to NORMAL.",
                    onHide: () => {
                        const settings = this.core.api.device_loadSettings();
                        settings.testable = false;
                        settings.bounceBackNotificationOverride = false;
                        settings.sponsorVideoSkipDelaySeconds = 10;
                        settings.bounceBackNotificationDelaySeconds = 60;
                        this.core.api.device_saveSettings(settings);
                        this.core.nav.navPageSearching(this.core.api);
                    }
                });
                this.wordTapStage = 0;
            } else if (this.wordTapSequence.endsWith("12321232123")) {
                this.showSetEnv();
                this.wordTapSequence = "";
            }
        }
    }

    private updateLogSettings(settings: Settings, lastSettings?: Settings) {
        if (!lastSettings || settings.enableLogging !== lastSettings.enableLogging) {
            if (settings.enableLogging) {
                Log.setLogBufferSize(settings.logBufferSize);
            } else {
                Log.setLogBufferSize(0);
            }
        } else if (settings.enableLogging) {
            if (settings.logBufferSize !== lastSettings.logBufferSize) {
                Log.setLogBufferSize(settings.logBufferSize);
            }
        }
        Log.setDefaultLevel(settings.logLevel);
    }

    private showSetEnv() {
        this.core.dialogs.choose({
            message: "Select the environment:\n(currently " + this.settings.env.toUpperCase() + ")",
            buttons: ["prod", "test"]
        }).then((result) => {
            if ((result === "prod" || result === "test") && result !== this.settings.env) {
                const settings = this.settings;
                this.core.api.user_setProperty(UserProperty.Environment, result);
                if (result === "prod") {
                    this.core.api.interaction(InteractionType.EnvProd, InteractionComponent.AboutPage);
                    settings.preconfigureSettings("production");
                } else if (result === "test") {
                    this.core.api.interaction(InteractionType.EnvTest, InteractionComponent.AboutPage);
                    settings.preconfigureSettings("testmode");
                }
                settings.env = result;
                this.core.api.device_saveSettings(settings);
                this.core.toast.toastInfo({
                    header: "Switch environment?",
                    message: "Changing to " + result.toUpperCase() + " environment...",
                    duration: 2000,
                    onHide: () => {
                        this.core.nav.navPageSearching(this.core.api);
                    }
                });
            }
        });
    }
}
